import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeBaseService, kbConsolidationService, type KnowledgeBaseItem, type ListRecordsResponse, type KBConsolidationRun, type ConsolidationExclude } from '../services/knowledgeBase.service';

export function useKnowledgeBaseTree(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};

  return useQuery<ListRecordsResponse<KnowledgeBaseItem>>({
    queryKey: ['knowledgeBaseItems'],
    queryFn: () => knowledgeBaseService.getTree(),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useKnowledgeBaseItem(id: string | null | undefined) {
  return useQuery<KnowledgeBaseItem>({
    queryKey: ['knowledgeBaseItem', id],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('Item ID is required'));
      return knowledgeBaseService.findById(id);
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useKnowledgeBaseSearch(query: string) {
  return useQuery<ListRecordsResponse<KnowledgeBaseItem>>({
    queryKey: ['knowledgeBaseSearch', query],
    queryFn: () => knowledgeBaseService.search(query),
    enabled: query.trim().length > 0,
    staleTime: 10 * 1000,
  });
}

export function useKnowledgeBaseTags() {
  return useQuery<string[]>({
    queryKey: ['knowledgeBaseTags'],
    queryFn: () => knowledgeBaseService.getTags(),
    staleTime: 60 * 1000,
  });
}

export function useCreateKnowledgeBaseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (item: Partial<KnowledgeBaseItem>) => knowledgeBaseService.create(item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItems'] });
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseTags'] });
    },
  });
}

export function useSaveKnowledgeBaseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (item: Partial<KnowledgeBaseItem>) => knowledgeBaseService.save(item),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItems'] });
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItem', data.id] });
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseTags'] });
    },
  });
}

export function useDeleteKnowledgeBaseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => knowledgeBaseService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItems'] });
    },
  });
}

export function useMoveKnowledgeBaseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newParentId }: { id: string; newParentId: string | null }) =>
      knowledgeBaseService.move(id, newParentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItems'] });
    },
  });
}

export function useApproveKnowledgeBaseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) => knowledgeBaseService.approve(id, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItems'] });
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItem'] });
    },
  });
}

export function useMarkPendingKnowledgeBaseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => knowledgeBaseService.markPending(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItems'] });
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItem'] });
    },
  });
}

export function useRejectKnowledgeBaseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => knowledgeBaseService.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItems'] });
    },
  });
}

// ----- Consolidation hooks -----

export function useStartKBConsolidation() {
  return useMutation({
    mutationFn: (force: boolean = false) =>
      kbConsolidationService.startConsolidation(force),
  });
}

export function useKBConsolidationRun(runId: string | null | undefined) {
  return useQuery<KBConsolidationRun>({
    queryKey: ['kbConsolidationRun', runId],
    queryFn: () => {
      if (!runId) return Promise.reject(new Error('Run ID is required'));
      return kbConsolidationService.getConsolidationRun(runId);
    },
    enabled: !!runId,
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' ? 2000 : false,
  });
}

export function useApplyKBConsolidation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ runId, exclude }: { runId: string; exclude?: ConsolidationExclude }) =>
      kbConsolidationService.applyConsolidation(runId, exclude),
    onSuccess: (run) => {
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItems'] });
      queryClient.invalidateQueries({ queryKey: ['knowledgeBaseItem'] });
      queryClient.setQueryData(['kbConsolidationRun', run.id], run);
    },
  });
}

export function useRejectKBConsolidation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (runId: string) => kbConsolidationService.rejectConsolidation(runId),
    onSuccess: (run) => {
      queryClient.setQueryData(['kbConsolidationRun', run.id], run);
    },
  });
}
